import _ from "lodash";
import { redirect } from "react-router-dom";
import {
  TemplateNavigationLevel,
  templateNavigationLevels,
  TemplateNavigationPath,
  TemplatesSummary,
  Version,
} from "../models/templates";
import store from "../store";
import { templatesActions } from "../store/templates-slice";
import { queryClient } from "../utils/http/queryClient";
import { fetchTemplate, fetchTemplatesSummary, fetchTemplateUsage, renderTemplate } from "../utils/http/templates";

const extractPathAndVersion = (
  params: string[]
): { selectedPath: TemplateNavigationPath; selectedVersion: Version } => {
  // Build selectedPath with formatting
  const selectedPath = templateNavigationLevels.reduce((acc, key, i) => {
    acc[key] = params[i];
    return acc;
  }, {} as TemplateNavigationPath);

  const selectedVersion = (params[3] || "draft") as Version;

  return { selectedPath, selectedVersion };
};

const checkPreceedingLevelsMatch = (selectedPath: TemplateNavigationPath, summary: TemplatesSummary, level: string) => {
  const levelIndex = templateNavigationLevels.indexOf(level as TemplateNavigationLevel);
  for (let i = 0; i < levelIndex; i++) {
    if (summary.navigationPath[templateNavigationLevels[i]] !== selectedPath[templateNavigationLevels[i]]) {
      return false;
    }
  }
  return true;
};

const hasVersion = (summary: TemplatesSummary) => {
  const filter = store.getState().templates.filter;

  let outcome = true;
  if (filter.hasDraftVersion === true) {
    outcome = outcome && summary.versions.includes("draft");
  }
  if (filter.hasLiveVersion === true) {
    outcome = outcome && summary.versions.includes("live");
  }

  return outcome;
};

const filterOptions = (
  selectedPath: TemplateNavigationPath,
  level: TemplateNavigationLevel,
  templatesSummary: TemplatesSummary[]
) => {
  let optionsBasedOnFilters = Array.from(
    new Set(
      templatesSummary
        .filter((t) => checkPreceedingLevelsMatch(selectedPath, t, level) && hasVersion(t))
        .map((t) => t.navigationPath[level])
    )
  ).sort();

  return optionsBasedOnFilters;
};

// USED TO BE COMMENTED ALREADY
// const updateAvailableOptions = (
//   level: TemplateLevel,
//   selectedPath: TemplatePath,
//   automationsConfigSummary: TemplatesSummary[],
//   levelData: Record<TemplateLevel, TemplatesLevelData>
// ) => {
//   const levelIndex = templateLevels.indexOf(level as TemplateLevel);

//   if (levelIndex === 0) {
//     return {
//       [level]: {
//         options: filterOptions(selectedPath, level, Object.keys(automations), automationsConfigSummary),
//         children: automations[selectedPath.namespace],
//       },
//     };
//   } else {
//     // The last level does not have children
//     let children = {};
//     if (levelIndex < flowLevels.length) {
//       children = levelData[flowLevels[levelIndex - 1]]?.children[selectedFlow[level]];
//       // Last level is stored as an array of strings, not an object.
//       children = Array.isArray(children) ? Object.fromEntries(children.map((v) => [v, {}])) : children;
//     }

//     return {
//       ...levelData,
//       [level]: {
//         options: filterOptions(
//           selectedFlow,
//           level,
//           Object.keys(levelData[flowLevels[levelIndex - 1]]?.children),
//           automationsConfigSummary
//         ),
//         children: children,
//       },
//     };
//   }
// };

export async function loaderTemplatesVerifyPathExistance({ path }: { path: string }): Promise<any> {
  const { selectedPath, selectedVersion } = extractPathAndVersion(path.split("/"));

  const templatesSummary: Array<TemplatesSummary> = await queryClient.fetchQuery({
    queryKey: ["templatesSummary"],
    queryFn: ({ signal }) => fetchTemplatesSummary({ signal }),
    staleTime: 2000,
  });

  // Value for the level is an empty string, unless we have something in the current path
  let finalNavigationPath: TemplateNavigationPath = {
    ...Object.fromEntries(Object.entries(selectedPath).map(([k, v]) => [k, v === undefined ? "" : v])),
  } as TemplateNavigationPath;

  // Check if what we have in the URL is possible, if not, take the first available option in the path
  templateNavigationLevels.forEach((level) => {
    let options = filterOptions(finalNavigationPath, level, templatesSummary);

    if (!options.includes(finalNavigationPath[level])) {
      finalNavigationPath[level] = options.length > 0 ? options[0] : "";
    }
  });

  // Check if the version exists. If not, draft is the preferred version
  const matchingTemplates = templatesSummary.filter(
    (t) =>
      t.navigationPath.namespace === finalNavigationPath.namespace &&
      t.navigationPath.category === finalNavigationPath.category &&
      t.navigationPath.name === finalNavigationPath.name &&
      hasVersion(t)
  );

  let finalVersion = selectedVersion;
  if (matchingTemplates.length > 0 && !matchingTemplates[0].versions.includes(finalVersion)) {
    if (matchingTemplates[0].versions.includes("draft")) {
      finalVersion = "draft";
    } else {
      finalVersion = "live";
    }
  }

  // If our selected flow and the correct flow are not equal, we redirect to the correct flow
  if (!_.isEqual(selectedPath, finalNavigationPath) || finalVersion !== selectedVersion) {
    return redirect(
      `/templates/${finalNavigationPath.namespace}/${finalNavigationPath.category}/${finalNavigationPath.name}/${finalVersion}`
    );
  }
}

export async function loaderTemplates({ path }: { path: string }): Promise<any> {
  const { selectedPath, selectedVersion } = extractPathAndVersion(path.split("/"));

  await queryClient.removeQueries({
    queryKey: ["mailTemplates", selectedPath.namespace, selectedPath.name, selectedVersion],
  });

  const templatesSummary: Array<TemplatesSummary> = await queryClient.fetchQuery({
    queryKey: ["templatesSummary"],
    queryFn: ({ signal }) => fetchTemplatesSummary({ signal }),
    staleTime: 2000,
  });

  // Define what are possible options per level
  let optionsPerLevel: Record<string, string[]> = {};
  templateNavigationLevels.forEach((level) => {
    optionsPerLevel[level] = filterOptions(selectedPath, level, templatesSummary);
  });

  const matchingTemplate =
    templatesSummary.find(
      (t) =>
        t.navigationPath.namespace === selectedPath.namespace &&
        t.navigationPath.category === selectedPath.category &&
        t.navigationPath.name === selectedPath.name
    ) || null;

  store.dispatch(templatesActions.setSelectedPath(selectedPath));
  store.dispatch(templatesActions.setSelectedTemplateSummary(matchingTemplate));
  store.dispatch(templatesActions.setSelectedVersion(selectedVersion));

  // Fetch the one specific template we selected right now
  const template = await queryClient.fetchQuery({
    queryKey: ["mailTemplates", selectedPath.namespace, selectedPath.name, selectedVersion],
    queryFn: ({ signal }) => fetchTemplate({ signal, key: selectedPath, version: selectedVersion }),
    staleTime: 2000,
  });

  store.dispatch(templatesActions.setTemplateExtendsOtherTemplate(template?.config?.extends_template !== undefined));

  // Render the one specific template we selected right now
  await queryClient.fetchQuery({
    queryKey: ["templateRender", selectedPath.namespace, selectedPath.name, selectedVersion],
    queryFn: ({ signal }) => renderTemplate({ signal, key: selectedPath, version: selectedVersion }),
    staleTime: 2000,
  });

  // Render the one specific template we selected right now
  await queryClient.fetchQuery({
    queryKey: ["templateUsedIn", selectedPath.namespace, selectedPath.name],
    queryFn: ({ signal }) => fetchTemplateUsage({ signal, key: selectedPath }),
    staleTime: 2000,
  });

  return {
    availableOptions: optionsPerLevel,
  };
}
